// 상단바 알림 벨 — 도메인 알림 (사건 마감 임박 / 메모 reminder / 정체 사건).
// 13-calendar-design.md §5 Phase 3.

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';

import MainCard from 'components/MainCard';
import IconButtonOrig from 'components/@extended/IconButton';
import TransitionsOrig from 'components/@extended/Transitions';

// Mantis 원본 컴포넌트의 props 타입이 부정확 (ref 강제 등) — 점진 정리 전까지 cast.
type LooseFC = React.FC<{ [key: string]: unknown; children?: React.ReactNode }>;
const Transitions = TransitionsOrig as unknown as LooseFC;
const IconButton = IconButtonOrig as unknown as LooseFC;

import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import FlagOutlined from '@ant-design/icons/FlagOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import WarningOutlined from '@ant-design/icons/WarningOutlined';

import { useAuth } from 'contexts/AuthContext';
import { useNotifications, type NotificationItem } from 'hooks/useNotifications';
import { markNotificationsRead } from 'firebase/users-service';

const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const iconForKind = (k: NotificationItem['kind']) => {
  switch (k) {
    case 'deadline':
      return <FlagOutlined />;
    case 'memo':
      return <FileTextOutlined />;
    case 'stagnant':
      return <WarningOutlined />;
  }
};

const avatarColor = (theme: Theme, color: NotificationItem['colorKey']) => {
  const c = theme.palette[color];
  return { color: c.main, bgcolor: c.light };
};

export default function Notification() {
  const downMD = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { user } = useAuth();

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  const { items, unreadCount } = useNotifications();

  const handleToggle = () => setOpen((p) => !p);
  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) return;
    setOpen(false);
  };

  const handleMarkAllRead = async () => {
    if (!user) return;
    try {
      await markNotificationsRead(user.uid);
    } catch {
      // rules/네트워크 에러 — silently ignore (다음 클릭에 재시도 가능)
    }
  };

  const handleItemClick = async (item: NotificationItem) => {
    // 항목 클릭 = 사용자가 알림을 보고 행동한 것 → Badge 즉시 0.
    // (의미: "지금 시점까지 본 알림은 모두 인지". readUntil 한 필드만 가져서
    //  individual read 불가 — 단순화.)
    if (user) {
      try {
        await markNotificationsRead(user.uid);
      } catch {
        // 권한/네트워크 — silently ignore. 다음에 재시도 가능.
      }
    }
    setOpen(false);
    navigate(item.navigateTo);
  };

  const displayItems = items.slice(0, 10); // 너무 많으면 잘림 — 전체보기로 캘린더 이동

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={{
          color: 'text.primary',
          bgcolor: open ? 'grey.100' : 'transparent'
        }}
        aria-label="알림"
        ref={anchorRef}
        aria-controls={open ? 'notification-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={unreadCount} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper
              sx={(theme: Theme) => ({
                boxShadow: (theme as Theme & { customShadows?: { z1?: string } }).customShadows?.z1 ?? theme.shadows[6],
                width: '100%',
                minWidth: 320,
                maxWidth: { xs: 320, md: 440 }
              })}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="알림"
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    unreadCount > 0 && (
                      <Tooltip title="모두 읽음">
                        <IconButton color="success" size="small" onClick={handleMarkAllRead}>
                          <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                        </IconButton>
                      </Tooltip>
                    )
                  }
                >
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      maxHeight: 480,
                      overflowY: 'auto',
                      '& .MuiListItemButton-root': {
                        py: 0.75,
                        px: 2,
                        '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
                        '& .MuiAvatar-root': avatarSX
                      }
                    }}
                  >
                    {displayItems.length === 0 && (
                      <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">알림이 없습니다</Typography>
                        <Typography variant="caption">임박 마감이 있으면 여기 표시됩니다</Typography>
                      </Box>
                    )}
                    {displayItems.map((it) => (
                      <ListItem
                        key={it.id}
                        component={ListItemButton}
                        divider
                        selected={it.isUnread}
                        onClick={() => handleItemClick(it)}
                      >
                        <ListItemAvatar>
                          <Avatar sx={(t: Theme) => avatarColor(t, it.colorKey)}>{iconForKind(it.kind)}</Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography
                              variant="subtitle2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {it.title}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {it.subtitle}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                    {items.length > displayItems.length && (
                      <ListItemButton
                        sx={{ textAlign: 'center', py: '12px !important' }}
                        onClick={() => {
                          setOpen(false);
                          navigate('/calendar');
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" color="primary">
                              전체 {items.length}건 · 캘린더에서 보기
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    )}
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
